/* eslint-disable react/jsx-props-no-spreading */
import React, { useRef, useEffect, useState, useContext } from 'react';
import { useDropzone } from 'react-dropzone';
import Spinner from '@carvana/showroom-spinner';
import { AnalyticsContext } from '@carvana/eventable-clickstream';
import FileItem from './FileItem';
import {
  SuccessMessage,
  LoadingDocument,
  DropContainer,
  Instructions,
  FileList,
  InstructionsSmall,
  Dropzone,
  UploadButton,
} from './styles/DocumentUpload.styles';
import validDocumentTypes from './validFileTypes';
import { isMobile } from '../utilities';
import { useDocumentUpload } from './useDocumentUpload';
import { RegistrationAnalytics } from '../../../constants';

type OwnProps = {
  tags?: any;
  allowSubmit?: boolean;
  children?: any;
};

const DocumentUpload = ({
  tags = [],
  allowSubmit = true,
  children,
}: OwnProps) => {
  const { trackEvent } = useContext(AnalyticsContext);
  const unsupportedFileTypeErrorCount = useRef(0);
  const [filesList, setFilesList] = useState([]);
  const [shouldShowSuccess, setShouldShowSuccess] = useState(false);
  const successTimeout = useRef<NodeJS.Timeout>();

  const {
    uploadDocument,
    removeDocument,
    uploadedFiles,
    loading,
  } = useDocumentUpload();
  const documentType = tags?.[1]?.[1] || '';

  useEffect(() => {
    setFilesList(Object.values(uploadedFiles));
  }, [uploadedFiles]);

  useEffect(() => {
    clearTimeout(successTimeout.current as NodeJS.Timeout);
    const updatedShouldShowSuccess =
      filesList.length > 0 &&
      filesList.every((file: any) => file.progress === 1);
    if (updatedShouldShowSuccess) {
      setShouldShowSuccess(updatedShouldShowSuccess);
      successTimeout.current = setTimeout(() => {
        setShouldShowSuccess(false);
      }, 5000);
    }
  }, [filesList]);

  useEffect(
    () => () => {
      clearTimeout(successTimeout.current as NodeJS.Timeout);
    },
    []
  );

  const processFiles = (validFiles: any, invalidFiles: any) => {
    const updatedFiles = [...validFiles, ...invalidFiles];

    if (allowSubmit) {
      uploadDocument(updatedFiles, tags);
      trackEvent(RegistrationAnalytics.UPLOADED_FILE, { documentType });

      if (invalidFiles.length) {
        unsupportedFileTypeErrorCount.current += 1;
      }
    }
  };

  const dialogOpened = () => {
    trackEvent(RegistrationAnalytics.CLICKED_UPLOAD, { documentType });
  };

  const { isDragAccept, getRootProps, getInputProps } = useDropzone({
    accept: validDocumentTypes,
    disabled: !allowSubmit,
    onDrop: processFiles,
    onFileDialogOpen: dialogOpened,
  });

  const retryUpload = (fileData: any) => () => {
    const { file } = fileData;
    trackEvent(RegistrationAnalytics.CLICKED_RETRY, file.name);
    uploadDocument([file], tags);
  };

  const getStatus = (file: any) => {
    if (file.warning) {
      return 'warning';
    }
    if (file.error === 'TYPE') {
      return 'typeError';
    }
    if (file.progress === 1) {
      return 'success';
    }
    if (file.error === 'SIZE') {
      return 'sizeError';
    }
    return 'loading';
  };

  return (
    <div>
      <DropContainer>
        <section>
          <Dropzone
            {...getRootProps({
              className: 'dropzone',
              isDragAccept,
              isMobile: isMobile(),
              allowSubmit,
            })}
          >
            {shouldShowSuccess && <SuccessMessage />}
            {children}
            <input {...getInputProps()} />
            {allowSubmit && (
              <>
                <Instructions>
                  {isMobile() ? (
                    <UploadButton size="small">Upload Files</UploadButton>
                  ) : (
                    <>
                      Drag and drop or{' '}
                      <span className="link">upload files</span>
                    </>
                  )}
                </Instructions>
                <InstructionsSmall>
                  To ensure that your documents get approved, please make sure
                  that photos are taken with good lighting and are not blurry.
                  File must be 21MB or less. We support the following file
                  types: .pdf, .doc, .docx, .png, .jpg, .tiff, .bmp, .gif
                </InstructionsSmall>
              </>
            )}
            {loading && (
              <LoadingDocument>
                <div>
                  <Spinner className="loading-spinner" />
                  <p>Uploading document...</p>
                </div>
              </LoadingDocument>
            )}
          </Dropzone>
        </section>
        <FileList>
          {filesList.map((file: any) => (
            <FileItem
              key={file.file.name}
              fileName={file.file.name}
              status={getStatus(file)}
              retry={retryUpload(file)}
              remove={() => removeDocument(file.file, documentType)}
            />
          ))}
        </FileList>
      </DropContainer>
    </div>
  );
};

export default DocumentUpload;
